import { zodResolver } from "@hookform/resolvers/zod";
import type { ColumnDef } from "@tanstack/react-table";
import { AlertCircle, ChevronDown, ChevronUp, MoreVertical, Plus, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { DataTable } from "@/components/admin/DataTable";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  useAdminFaqs,
  useCreateFaq,
  useDeleteFaq,
  useMoveFaq,
  useUpdateFaq,
} from "@/hooks/admin/faqs";
import { AdminLayout } from "@/layouts/AdminLayout";
import type { FaqEntry } from "@/types";

const faqFormSchema = z.object({
  question: z.string().trim().min(1, "Question is required"),
  answer: z.string().trim().min(1, "Answer is required"),
  is_published: z.boolean(),
});
type FaqFormValues = z.infer<typeof faqFormSchema>;

const EMPTY_FAQ: FaqFormValues = { question: "", answer: "", is_published: true };

function FaqFormModal({ faq, onClose }: { faq?: FaqEntry; onClose: () => void }) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const createFaq = useCreateFaq();
  const updateFaq = useUpdateFaq();
  const form = useForm<FaqFormValues>({
    resolver: zodResolver(faqFormSchema),
    defaultValues: faq
      ? { question: faq.question, answer: faq.answer, is_published: faq.is_published }
      : EMPTY_FAQ,
  });
  const isSubmitting = form.formState.isSubmitting;

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    const payload = {
      question: values.question.trim(),
      answer: values.answer.trim(),
      is_published: values.is_published,
    };
    try {
      if (faq) {
        await updateFaq.mutateAsync({ id: faq.id, data: payload });
      } else {
        await createFaq.mutateAsync(payload);
      }
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to save FAQ entry");
    }
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-2xl bg-white text-gray-900 shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 p-6">
          <h2 className="font-semibold text-gray-900 text-xl">
            {faq ? "Edit FAQ Entry" : "Add FAQ Entry"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 transition-colors hover:bg-gray-100"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <Form {...form}>
          <form onSubmit={onSubmit} className="max-h-[calc(90vh-80px)] overflow-y-auto p-6">
            {submitError && (
              <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">{submitError}</span>
              </div>
            )}
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="question"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Question<span className="text-red-500"> *</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="answer"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Answer<span className="text-red-500"> *</span>
                    </FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={6} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="is_published"
                render={({ field }) => (
                  <FormItem>
                    <label className="flex cursor-pointer items-center gap-2">
                      <Checkbox
                        checked={Boolean(field.value)}
                        onCheckedChange={(c) => field.onChange(c === true)}
                      />
                      <span className="text-sm text-gray-700">
                        Published — {field.value ? "visible on the site" : "hidden from the site"}
                      </span>
                    </label>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-6">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-4 py-2 text-gray-700 transition-colors hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-lg bg-primary px-4 py-2 font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
              >
                {isSubmitting ? "Saving…" : faq ? "Save Changes" : "Add FAQ Entry"}
              </button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}

function DeleteFaqModal({ faq, onClose }: { faq: FaqEntry | null; onClose: () => void }) {
  const deleteFaq = useDeleteFaq();
  const [error, setError] = useState<string | null>(null);

  if (!faq) return null;

  const handleDelete = async () => {
    setError(null);
    try {
      await deleteFaq.mutateAsync(faq.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete FAQ entry");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white text-gray-900 rounded-2xl w-full max-w-md shadow-2xl p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Delete FAQ Entry</h2>
        <p className="text-gray-600 mb-4">
          Are you sure you want to delete <strong>{faq.question}</strong>? This cannot be undone.
        </p>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <span className="text-sm">{error}</span>
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleteFaq.isPending}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white rounded-lg transition-colors font-medium"
          >
            {deleteFaq.isPending ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminFaqsPage() {
  const [faqModal, setFaqModal] = useState<{ open: boolean; faq?: FaqEntry }>({ open: false });
  const [deletingFaq, setDeletingFaq] = useState<FaqEntry | null>(null);

  const faqsQuery = useAdminFaqs();
  const faqs = faqsQuery.data ?? [];
  const error = faqsQuery.isError
    ? faqsQuery.error instanceof Error
      ? faqsQuery.error.message
      : "Failed to load FAQ entries"
    : null;

  const moveFaq = useMoveFaq();
  const { mutate: mutateUpdateFaq } = useUpdateFaq();

  const columns = useMemo<ColumnDef<FaqEntry>[]>(
    () => [
      {
        id: "order",
        header: "",
        cell: ({ row }) => {
          const index = row.index;
          return (
            <div className="flex flex-col">
              <button
                type="button"
                aria-label={`Move "${row.original.question}" up`}
                disabled={index === 0 || moveFaq.isPending}
                onClick={() => moveFaq.mutate({ id: row.original.id, direction: "up" })}
                className="rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:pointer-events-none disabled:opacity-30"
              >
                <ChevronUp className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label={`Move "${row.original.question}" down`}
                disabled={index === faqs.length - 1 || moveFaq.isPending}
                onClick={() => moveFaq.mutate({ id: row.original.id, direction: "down" })}
                className="rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:pointer-events-none disabled:opacity-30"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
          );
        },
      },
      {
        accessorKey: "question",
        header: "Question",
        cell: ({ row }) => (
          <div className="max-w-2xl">
            <div className="font-medium text-gray-900">{row.original.question}</div>
            <div className="mt-0.5 line-clamp-2 text-xs text-gray-500">{row.original.answer}</div>
          </div>
        ),
      },
      {
        accessorKey: "is_published",
        header: "Status",
        cell: ({ row }) => (
          <StatusBadge tone={row.original.is_published ? "success" : "neutral"}>
            {row.original.is_published ? "Published" : "Hidden"}
          </StatusBadge>
        ),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => {
          const faq = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="FAQ actions"
                  className="rounded-lg p-1.5 transition-colors hover:bg-gray-100"
                >
                  <MoreVertical className="h-4 w-4 text-gray-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setFaqModal({ open: true, faq })}>
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    mutateUpdateFaq({ id: faq.id, data: { is_published: !faq.is_published } })
                  }
                >
                  {faq.is_published ? "Hide" : "Publish"}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setDeletingFaq(faq)}
                  className="text-red-600 focus:text-red-600"
                >
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [faqs.length, moveFaq, mutateUpdateFaq]
  );

  return (
    <AdminLayout>
      <div className="flex h-full flex-col gap-4">
        <DataTable
          className="h-full"
          columns={columns}
          data={faqs}
          isLoading={faqsQuery.isPending}
          error={error}
          onRetry={() => faqsQuery.refetch()}
          emptyMessage="No FAQ entries yet"
          getRowId={(f) => f.id}
          toolbar={
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-gray-500">
                Shown on the website and app FAQ screens in this order. Changes go live immediately.
              </p>
              <button
                type="button"
                onClick={() => setFaqModal({ open: true })}
                className="flex flex-shrink-0 items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
              >
                <Plus className="h-4 w-4" />
                Add FAQ Entry
              </button>
            </div>
          }
        />
      </div>

      {faqModal.open && (
        <FaqFormModal faq={faqModal.faq} onClose={() => setFaqModal({ open: false })} />
      )}

      <DeleteFaqModal faq={deletingFaq} onClose={() => setDeletingFaq(null)} />
    </AdminLayout>
  );
}
